<template>
  <section class="partner-profile">
    <div class="partner-profile__hero">
      <div>
        <p class="partner-profile__eyebrow">Ficha de socio</p>
        <h2 class="partner-profile__title">{{ currentPartner.name || 'Nuevo socio' }}</h2>
        <p class="partner-profile__copy">
          Administra los datos base del socio y sus credenciales personales sin salir de la ficha del cliente.
        </p>
      </div>

      <div v-if="currentPartner.id" class="partner-profile__summary">
        <span>{{ formattedRut }}</span>
        <span>{{ formattedParticipation }}</span>
      </div>
    </div>

    <article class="partner-profile__card">
      <div class="partner-profile__section-head">
        <div>
          <p class="partner-profile__section-eyebrow">Datos base</p>
          <h3 class="partner-profile__section-title">Identificación y participación</h3>
        </div>
      </div>

      <PartnerEditor
        :value="currentPartner"
        :company-id="companyId"
        @saved="handlePartnerSaved"
        @cancel="$emit('cancel')"
      />
    </article>

    <article class="partner-profile__card">
      <div class="partner-profile__section-head">
        <div>
          <p class="partner-profile__section-eyebrow">Credenciales personales</p>
          <h3 class="partner-profile__section-title">Claves y accesos del socio</h3>
        </div>

        <button
          v-if="permissions.canCreateSecret"
          class="partner-profile__primary-action"
          type="button"
          :disabled="!currentPartner.person_id"
          @click="openSecretEditor()"
        >
          Nueva clave
        </button>
      </div>

      <p v-if="!currentPartner.person_id" class="partner-profile__hint">
        Guarda primero el socio para habilitar sus claves personales.
      </p>

      <div v-else-if="isLoadingSecrets" class="partner-profile__empty">
        Cargando claves personales...
      </div>

      <template v-else>
        <div v-if="secretEditorOpen" class="partner-profile__secret-editor">
          <SecretEditor
            :value="selectedSecret"
            :company-id="companyId"
            :person-id="currentPartner.person_id"
            @saved="handleSecretSaved"
            @cancel="closeSecretEditor"
          />
        </div>

        <div v-if="personalSecrets.length" class="partner-profile__vault">
          <article
            v-for="secret in personalSecrets"
            :key="secret.id"
            class="partner-profile__secret-card"
          >
            <div class="partner-profile__secret-main">
              <div>
                <h4 class="partner-profile__secret-name">{{ secret.name || 'Acceso sin nombre' }}</h4>
                <p class="partner-profile__secret-meta">
                  {{ secret.category || 'General' }}
                  <span v-if="secret.owner">· {{ secret.owner }}</span>
                </p>
              </div>

              <span
                v-if="secret.needs_rotation"
                class="partner-profile__flag"
              >
                Rotación pendiente
              </span>
            </div>

            <div class="partner-profile__secret-field">
              <span class="partner-profile__field-label">Usuario</span>
              <span class="partner-profile__field-value">{{ secret.username || 'No informado' }}</span>
            </div>

            <div class="partner-profile__secret-field">
              <span class="partner-profile__field-label">Valor</span>
              <span class="partner-profile__field-value partner-profile__field-value--masked">{{ getMaskedValue(secret) }}</span>
            </div>

            <div class="partner-profile__secret-footer">
              <div class="partner-profile__secret-dates">
                <span>Actualizado: {{ formatDate(secret.updated_at) }}</span>
                <span v-if="secret.last_used_at">Ultimo uso: {{ formatDate(secret.last_used_at) }}</span>
              </div>

              <div class="partner-profile__actions">
                <button
                  v-if="permissions.canRevealSecret"
                  class="partner-profile__ghost-action"
                  type="button"
                  @click="$emit('reveal-secret', secret)"
                >
                  Mostrar
                </button>

                <button
                  v-if="permissions.canRevealSecret"
                  class="partner-profile__ghost-action"
                  type="button"
                  @click="$emit('copy-secret', secret)"
                >
                  Copiar
                </button>

                <button
                  v-if="permissions.canEditSecret"
                  class="partner-profile__ghost-action"
                  type="button"
                  @click="openSecretEditor(secret)"
                >
                  Editar
                </button>

                <button
                  v-if="permissions.canDeleteSecret"
                  class="partner-profile__danger-action"
                  type="button"
                  @click="$emit('delete-secret', secret)"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="partner-profile__empty">
          No hay claves personales registradas para este socio.
        </div>
      </template>
    </article>
  </section>
</template>

<script>
import PartnerEditor from '@/components/company/editors/PartnerEditor.vue'
import SecretEditor from '@/components/company/editors/SecretEditor.vue'
import companyAccessService from '@/services/company/companyAccess.service'
import { formatRut } from '@/utils/rut'

export default {
  name: 'PartnerProfileEditor',
  components: {
    PartnerEditor,
    SecretEditor
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    permissions: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      currentPartner: this.value ? { ...this.value } : {},
      personalSecrets: [],
      isLoadingSecrets: false,
      secretEditorOpen: false,
      selectedSecret: null
    }
  },
  computed: {
    formattedRut () {
      return this.currentPartner && this.currentPartner.rut ? formatRut(this.currentPartner.rut) : 'Sin RUT'
    },
    formattedParticipation () {
      const value = Number(this.currentPartner && this.currentPartner.participation_perc)
      return Number.isFinite(value) ? `${value}% de participación` : 'Participación sin definir'
    }
  },
  watch: {
    value: {
      immediate: true,
      async handler (newValue) {
        this.currentPartner = newValue ? { ...newValue } : {}
        this.secretEditorOpen = false
        this.selectedSecret = null
        await this.loadPersonalSecrets()
      }
    }
  },
  methods: {
    async loadPersonalSecrets () {
      if (!this.currentPartner || !this.currentPartner.person_id) {
        this.personalSecrets = []
        return
      }

      this.isLoadingSecrets = true
      try {
        const response = await companyAccessService.getPersonAccesses(this.currentPartner.person_id)
        this.personalSecrets = response.secrets
      } catch (error) {
        console.log(error)
        this.personalSecrets = []
      } finally {
        this.isLoadingSecrets = false
      }
    },
    async handlePartnerSaved (partner) {
      this.currentPartner = { ...partner }
      await this.loadPersonalSecrets()
      this.$emit('saved', partner)
      this.$emit('refresh')
    },
    openSecretEditor (secret = null) {
      this.selectedSecret = secret ? { ...secret } : null
      this.secretEditorOpen = true
    },
    closeSecretEditor () {
      this.secretEditorOpen = false
      this.selectedSecret = null
    },
    async handleSecretSaved () {
      this.closeSecretEditor()
      await this.loadPersonalSecrets()
      this.$emit('refresh')
    },
    formatDate (value) {
      return value ? new Date(value).toLocaleString() : 'Sin registro'
    },
    getMaskedValue (secret) {
      if (secret.masked_value) return secret.masked_value
      if (secret.secret && typeof secret.secret === 'string') {
        return '•'.repeat(Math.max(8, Math.min(secret.secret.length, 14)))
      }
      return '••••••••'
    },
  }
}
</script>

<style scoped>
.partner-profile {
  display: grid;
  gap: 1rem;
}

.partner-profile__hero,
.partner-profile__card {
  padding: 1rem;
  border: 1px solid rgba(95, 76, 52, 0.1);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 251, 245, 0.96) 0%, rgba(255, 255, 255, 0.98) 100%);
}

.partner-profile__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.partner-profile__eyebrow,
.partner-profile__section-eyebrow,
.partner-profile__field-label {
  margin: 0;
  color: rgba(74, 58, 45, 0.72);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.partner-profile__title,
.partner-profile__section-title,
.partner-profile__secret-name {
  margin: 0;
  color: #241c16;
}

.partner-profile__title {
  margin-top: 0.2rem;
  font-size: 1.35rem;
}

.partner-profile__copy,
.partner-profile__hint,
.partner-profile__empty,
.partner-profile__secret-meta,
.partner-profile__secret-dates,
.partner-profile__field-value {
  margin: 0;
  color: rgba(45, 35, 26, 0.72);
}

.partner-profile__summary {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.partner-profile__summary span,
.partner-profile__flag {
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  background: rgba(90, 70, 48, 0.08);
  color: #3e2f22;
  font-size: 0.82rem;
  font-weight: 600;
}

.partner-profile__section-head,
.partner-profile__secret-main,
.partner-profile__secret-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
}

.partner-profile__primary-action,
.partner-profile__ghost-action,
.partner-profile__danger-action {
  min-height: 2.5rem;
  padding: 0 0.95rem;
  border-radius: 999px;
  font-weight: 600;
}

.partner-profile__primary-action {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

.partner-profile__ghost-action {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.partner-profile__danger-action {
  border: 1px solid rgba(162, 68, 68, 0.16);
  background: rgba(255, 243, 243, 0.9);
  color: #8f3333;
}

.partner-profile__secret-editor,
.partner-profile__vault {
  margin-top: 1rem;
}

.partner-profile__vault {
  display: grid;
  gap: 0.8rem;
}

.partner-profile__secret-card {
  display: grid;
  gap: 0.75rem;
  padding: 0.95rem;
  border: 1px solid rgba(95, 76, 52, 0.08);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
}

.partner-profile__secret-field {
  display: grid;
  gap: 0.15rem;
}

.partner-profile__field-value--masked {
  letter-spacing: 0.08em;
}

.partner-profile__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
}

@media (max-width: 720px) {
  .partner-profile__hero,
  .partner-profile__section-head,
  .partner-profile__secret-main,
  .partner-profile__secret-footer {
    flex-direction: column;
  }

  .partner-profile__summary {
    justify-content: flex-start;
  }

  .partner-profile__actions {
    justify-content: flex-start;
  }
}
</style>
